import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CaretLeft, CaretRight, CaretRight as ChevronRight, MapPin, BookOpen, Question } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import ProviderCard from "../components/ProviderCard";
import { api } from "../lib/api";
import { getSpecialtyContent, SPECIALTY_DIRECTORY_FAQS, ALL_SPECIALTIES, ALL_STATES } from "../lib/seoContent";
import { medicalWebPageSchema, richItemListSchema, breadcrumbSchema } from "../lib/jsonLd";

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "District of Columbia" }
];

const SPECIALTY_DISPLAY_NAMES = {
  "cardiology": "Cardiology",
  "dermatology": "Dermatology", 
  "pediatrics": "Pediatrics",
  "psychiatry": "Psychiatry",
  "orthopedics": "Orthopedics",
  "family-medicine": "Family Medicine",
  "internal-medicine": "Internal Medicine",
  "neurology": "Neurology",
  "oncology": "Oncology",
  "radiology": "Radiology"
};

const ITEMS_PER_PAGE = 20;

const SpecialtyDirectory = () => {
  const { specialty } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState(searchParams.get("state") || "");
  const [cityFilter, setCityFilter] = useState(searchParams.get("city") || "");
  const [entityType, setEntityType] = useState(searchParams.get("type") || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  const specialtyName = SPECIALTY_DISPLAY_NAMES[specialty] || specialty.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: ITEMS_PER_PAGE,
        skip: (currentPage - 1) * ITEMS_PER_PAGE
      };
      if (stateFilter && stateFilter !== "all") {
        params.state = stateFilter;
      }
      if (cityFilter.trim()) {
        params.city = cityFilter.trim();
      }
      if (entityType && entityType !== "all") {
        params.enumeration_type = entityType;
      }
      
      const data = await api.getProvidersBySpecialty(specialty, params);
      setResults(data.results || []);
      setTotalCount(data.result_count || 0);
      setHasMore(data.has_more || false);
    } catch (error) {
      console.error("Error fetching specialty providers:", error);
      setResults([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [specialty, stateFilter, cityFilter, entityType, currentPage]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handleStateChange = (value) => {
    setStateFilter(value);
    setCurrentPage(1);
    const newParams = {};
    if (value && value !== "all") newParams.state = value;
    if (cityFilter.trim()) newParams.city = cityFilter.trim();
    if (entityType && entityType !== "all") newParams.type = entityType;
    setSearchParams(newParams);
  };

  const handleCitySearch = () => {
    setCurrentPage(1);
    const newParams = {};
    if (stateFilter && stateFilter !== "all") newParams.state = stateFilter;
    if (cityFilter.trim()) newParams.city = cityFilter.trim();
    if (entityType && entityType !== "all") newParams.type = entityType;
    setSearchParams(newParams);
  };

  const handleEntityTypeChange = (value) => {
    setEntityType(value);
    setCurrentPage(1);
    const newParams = {};
    if (stateFilter && stateFilter !== "all") newParams.state = stateFilter;
    if (cityFilter.trim()) newParams.city = cityFilter.trim();
    if (value && value !== "all") newParams.type = value;
    setSearchParams(newParams);
  };

  const specialtyContent = getSpecialtyContent(specialty);
  const faqs = SPECIALTY_DIRECTORY_FAQS(specialtyName, null);
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <title>{specialtyName + " NPI Lookup — Find " + specialtyName + " Doctors & NPI Numbers"}</title>
        <meta 
          name="description" 
          content={specialtyName + " NPI lookup — search " + totalCount.toLocaleString() + "+ " + specialtyName.toLowerCase() + " physicians by NPI number, verify NPPES credentials, and find specialist doctors across the United States."}
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(medicalWebPageSchema(specialtyName, "Find " + specialtyName + " healthcare providers in the United States.", "/specialty/" + specialty))}</script>
        <script type="application/ld+json">{JSON.stringify(richItemListSchema(
          `${specialtyName} Providers`,
          `Verified ${specialtyName} physicians with NPI numbers from the NPPES national registry.`,
          results,
          `/specialty/${specialty}`
        ))}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([{ name: "Home", url: "/" }, { name: specialtyName }]))}</script>
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm" data-testid="specialty-breadcrumb">
            <ol className="flex items-center gap-2 text-slate-500">
              <li><Link to="/" className="hover:text-teal">Home</Link></li>
              <li>/</li>
              <li className="text-slate-900 font-medium">{specialtyName}</li>
            </ol>
          </nav>

          {/* Header */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 sm:p-8 mb-8">
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-navy mb-2" data-testid="specialty-title">
              {specialtyName} Providers
            </h1>
            <p className="text-slate-500">
              Browse healthcare providers specializing in {specialtyName.toLowerCase()}
            </p>
            
            {/* Filters */}
            <div className="mt-6 flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">State</label>
                <Select value={stateFilter} onValueChange={handleStateChange}>
                  <SelectTrigger className="w-full sm:w-52" data-testid="specialty-state-filter">
                    <SelectValue placeholder="All states" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All states</SelectItem>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">City</label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="e.g., Miami, Chicago..."
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCitySearch()}
                    className="w-full sm:w-52 h-9"
                    data-testid="specialty-city-filter"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCitySearch}
                    className="h-9 px-4"
                    data-testid="specialty-city-search-btn"
                  >
                    Search
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Provider Type</label>
                <Select value={entityType} onValueChange={handleEntityTypeChange}>
                  <SelectTrigger className="w-full sm:w-44 h-9" data-testid="specialty-entity-filter">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="NPI-1">Individual</SelectItem>
                    <SelectItem value="NPI-2">Organization</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <span className="text-sm text-slate-500 pb-1" data-testid="specialty-result-count">
                {loading ? "Loading..." : `Showing ${results.length} providers per page`}
              </span>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="skeleton h-4 w-20 mb-3 rounded" />
                  <div className="skeleton h-6 w-3/4 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/2 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/3 mb-4 rounded" />
                  <div className="skeleton h-10 w-full rounded" />
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                No providers found
              </h3>
              <p className="text-slate-500">
                Try selecting a different state or check back later.
              </p>
            </div>
          ) : (
            <>
              <div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="specialty-results-grid"
              >
                {results.map((provider) => (
                  <ProviderCard key={provider.number} provider={provider} />
                ))}
              </div>

              {/* Pagination */}
              {(hasMore || currentPage > 1) && (
                <div className="flex items-center justify-center gap-3 mt-8" data-testid="specialty-pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    <CaretLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  
                  <span className="text-sm text-slate-500 font-medium px-3">
                    Page {currentPage}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={!hasMore}
                    className="pagination-btn"
                  >
                    Next
                    <CaretRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Browse by State Grid - Major internal linking for SEO */}
          <div className="mt-10" data-testid="browse-by-state-section">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-navy" />
                <h2 className="font-display font-semibold text-xl text-slate-900">
                  Find {specialtyName} Providers by State
                </h2>
              </div>
              <p className="text-sm text-slate-500 mb-6">
                Browse {specialtyName.toLowerCase()} providers in every US state
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {ALL_STATES.map(st => (
                  <Link
                    key={st.code}
                    to={`/specialty/${specialty}/${st.code.toLowerCase()}`}
                    className="px-3 py-2 text-sm bg-slate-50 rounded-lg hover:bg-teal/10 hover:text-teal text-slate-700 transition-colors text-center"
                    data-testid={`state-link-${st.code}`}
                  >
                    {st.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* About This Specialty - Educational SEO Content */}
          {specialtyContent && (
            <div className="mt-8" data-testid="about-specialty-section">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-5 h-5 text-navy" />
                  <h2 className="font-display font-semibold text-xl text-slate-900">
                    About {specialtyContent.title}
                  </h2>
                </div>
                <div className="space-y-4">
                  <p className="text-slate-600 leading-relaxed">{specialtyContent.description}</p>
                  <h3 className="font-display font-semibold text-base text-slate-900 mt-4">
                    What Does a {specialtyContent.title} Specialist Do?
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{specialtyContent.whatTheyDo}</p>
                  <h3 className="font-display font-semibold text-base text-slate-900 mt-4">
                    When Should You See a {specialtyContent.title} Specialist?
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{specialtyContent.whenToSee}</p>
                  <h3 className="font-display font-semibold text-base text-slate-900 mt-4">
                    Education & Training
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{specialtyContent.education}</p>
                </div>
              </div>
            </div>
          )}

          {/* Related Specialties - Cross-linking */}
          <div className="mt-8" data-testid="related-specialties-section">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="font-display font-semibold text-xl text-slate-900 mb-4">
                Browse Other Specialties
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {ALL_SPECIALTIES.filter(s => s.slug !== specialty).map(s => (
                  <Link
                    key={s.slug}
                    to={`/specialty/${s.slug}`}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-teal/10 hover:text-teal text-slate-700 text-sm transition-colors"
                  >
                    <span>{s.name}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-8 mb-8" data-testid="specialty-faq-section">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <Question className="w-5 h-5 text-navy" weight="bold" />
                <h2 className="font-display font-semibold text-xl text-slate-900">
                  {specialtyName} FAQ
                </h2>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <details key={i} className="group border border-slate-200 rounded-lg">
                    <summary className="flex items-center justify-between p-4 cursor-pointer select-none hover:bg-slate-50 transition-colors rounded-lg">
                      <span className="font-medium text-slate-900 text-sm pr-4">{faq.question}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                    </summary>
                    <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{faq.answer}</div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpecialtyDirectory;
